import React from "react";
import { BsPlusCircle, BsSearch, BsArrowClockwise } from "react-icons/bs";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { Vehicle } from "../../../../models";
import {
  getVehicles,
  toggleShowAddVehicleModal,
  toggleShowEditVehicleModal,
  showDeleteVehicleConfirmationModal,
  setSelectedVehicle,
} from "../../vehiclesSlice";
import { relativeTime } from "../../vehicleStatus";
import { VehiclesTable } from "../../components/VehiclesTable/VehiclesTable";
import { VehiclesMap } from "../../components/VehiclesMap/VehiclesMap";

import "./VehiclesView.scss";

const POLL_INTERVAL_MS = 60_000;
const STALE_AFTER_MS = 2 * POLL_INTERVAL_MS;

export function VehiclesView() {
  const dispatch = useAppDispatch();

  const vehicles = useAppSelector((state) => state.vehicles.vehicles) || [];
  const getVehiclesAPI = useAppSelector((state) => state.vehicles.getVehicles);
  const deleteVehicleAPI = useAppSelector((state) => state.vehicles.deleteVehicle);
  const vehicleToRemove = useAppSelector((state) => state.vehicles.vehicleToRemove);
  const lastUpdatedAt = useAppSelector((state) => state.vehicles.lastUpdatedAt);

  const [search, setSearch] = React.useState<string>("");
  const [nowTick, setNowTick] = React.useState<number>(0); // re-render the freshness label each second
  const inFlight = React.useRef(false);

  const poll = React.useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    dispatch(getVehicles({ withStats: true })).finally(() => {
      inFlight.current = false;
    });
  }, [dispatch]);

  // Fetch on mount + every 60s; clear on unmount.
  React.useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [poll]);

  // Tick the "Updated Xs ago" label once a second.
  React.useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    if (!getVehiclesAPI.loading && getVehiclesAPI.error) {
      toast.error("Failed to refresh vehicles. Showing last known data.");
    }
  }, [getVehiclesAPI.loading, getVehiclesAPI.error]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearch(e.target.value);
  };

  const onAddVehicle = () => dispatch(toggleShowAddVehicleModal());

  const onEditVehicle = (vehicle: Vehicle) => {
    dispatch(setSelectedVehicle(vehicle));
    dispatch(toggleShowEditVehicleModal());
  };

  const onDeleteVehicle = (vehicle: Vehicle) => {
    dispatch(showDeleteVehicleConfirmationModal(vehicle));
  };

  const data = React.useMemo(() => {
    if (search === "") return vehicles;
    return vehicles.filter((v) => v.label.toLowerCase().includes(search.toLowerCase()));
  }, [vehicles, search]);

  const freshness = React.useMemo(() => {
    void nowTick; // dependency so the label recomputes each tick
    if (!lastUpdatedAt) return "";
    const age = Date.now() - Date.parse(lastUpdatedAt);
    return age > STALE_AFTER_MS ? "Stale" : `Updated ${relativeTime(lastUpdatedAt)}`;
  }, [lastUpdatedAt, nowTick]);

  const deletingId =
    deleteVehicleAPI.loading && vehicleToRemove ? vehicleToRemove : undefined;

  // Initial-load spinner only — never blank last-good data on a background poll.
  if (getVehiclesAPI.loading && vehicles.length === 0) {
    return (
      <div className="vehicles-view-component">
        <div className="initial-loading">
          <Oval color="#131313" height={40} width={40} secondaryColor="#FBD20A" />
        </div>
      </div>
    );
  }

  return (
    <div className="vehicles-view-component">
      <div className="controllers-container">
        <div className="left-controllers-container">
          <div className="title-container">
            <div className="title-text">Vehicles</div>
            <div className="title-count">{data.length}</div>
          </div>

          <div className="search-container">
            <BsSearch className="search-icon" />
            <input value={search} placeholder="Search" onChange={onInputChange} />
          </div>
        </div>

        <div className="right-controllers-container">
          {freshness ? (
            <div className={`freshness-indicator${freshness === "Stale" ? " stale" : ""}`}>
              {freshness}
            </div>
          ) : null}

          <div className="refresh-button" onClick={poll} role="button" aria-label="Refresh">
            <BsArrowClockwise className={`icon${getVehiclesAPI.loading ? " spinning" : ""}`} />
            Refresh
          </div>

          <div className="add-button" onClick={onAddVehicle}>
            <BsPlusCircle className="icon" />
            Add Vehicle
          </div>
        </div>
      </div>

      <div className="map-container">
        <VehiclesMap vehicles={vehicles} />
      </div>

      <div className="table-container">
        <VehiclesTable
          data={data}
          onEdit={onEditVehicle}
          onDelete={onDeleteVehicle}
          deletingId={deletingId}
        />
      </div>
    </div>
  );
}
